import type {
  ClientPerson,
  ClientPersonReference
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, type FC } from 'react';
import { IconButton } from 'react-native-paper';

import type { ProfileAvatarProps } from '@/features/media/components/avatar/ProfileAvatar';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import type { MediaItemSize } from '@/features/media/types/mediaItem';
import {
  determineBestMediaItemSize,
  getSizedMediaItemUrl
} from '@/features/media/utils/mediaItem';
import {
  getPersonAvatarPicture,
  getPersonAvatarTextFallback
} from '@/features/media/utils/person';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { AppRoute } from '@/navigation/types/navigation';
import { isEmptyOrNull } from '@/shared/utils/string';

type Props = {
  person: ClientPerson | ClientPersonReference;
  size?: number;
  mediaSize?: MediaItemSize;
  borderWidth?: ProfileAvatarProps['borderWidth'];
  canOpenGallery?: boolean;
};

/**
 * Specialized component for displaying a person's profile picture.
 * Handles deleted and blocked persons.
 * Should be preferred over {@link ProfileAvatar}
 */
export const PersonAvatar: FC<Props> = ({
  person,
  size = 56,
  mediaSize,
  borderWidth = 0,
  canOpenGallery = false
}) => {
  const navigation = useAppNavigation();
  const picture = getPersonAvatarPicture(person);

  const handlePicturePress = useCallback(() => {
    const pictureUrl = getSizedMediaItemUrl(picture)?.url;

    if (isEmptyOrNull(pictureUrl)) return;
    navigation.navigate(AppRoute.Gallery, { mediaUrls: [pictureUrl] });
  }, [navigation, picture]);

  return (
    <IconButton
      icon={() => (
        <ProfileAvatar
          viewSize={size}
          mediaItem={picture}
          mediaSize={mediaSize ?? determineBestMediaItemSize(size)}
          textFallback={getPersonAvatarTextFallback(person)}
          borderWidth={borderWidth}
        />
      )}
      size={size + borderWidth}
      onPress={canOpenGallery && picture ? handlePicturePress : undefined}
      // Note: Margin -8 is needed as icon button adds additional padding for highlighting when pressed but here we need the passed size as view size
      style={{ margin: -8 }}
    />
  );
};
